import BtnPrimary from 'components/generic/BtnPrimary'
import React, { useEffect, useMemo, useState } from 'react'
import SelectAvatarModal from './SelectAvatarModal'
import styles from './myDetails.module.scss'
import { TennisIdIcon } from 'components/Icons'
import i18n from 'constants/strings'
import { useDispatch, useSelector } from 'react-redux'
import { Form, Formik } from 'formik'
import RadioFieldWTN from 'components/generic/RadioFieldWTN'
import BtnToggle from 'components/generic/BtnToggle'
import { AppState } from 'redux/reducers'
import { useCountries } from 'hooks/use-countries'
import {
  FAVOURITE_SHOT_OPTIONS,
  HEIGHT_UNIT_OPTIONS,
  UnitSystem,
  CoreFieldsMapping,
  EDITABLE_BIOGRAPHICAL_FIELDS,
  CoreFieldKey,
} from 'forms/my-account'
import {
  formatUiDate,
  initializeHeightValues,
  isFormattedDateValid,
  splitFormattedDate,
} from 'utils'
import InputWTN from 'components/generic/InputWTN'
import SelectWTN from 'components/generic/SelectWTN'
import SearchFieldWTN, { Option } from 'components/generic/SearchFieldWTN'
import HeightUnitInputs from 'components/generic/HeightUnitInputs'
import { getEditableFields } from 'api/graphql/data-transforms'
import { PlayingDoubleHandEnum, PlayingHandEnum, SexEnum } from 'types/api/enums'
import InfoBox from 'components/generic/InfoBox'
import BtnSecondary from 'components/generic/BtnSecondary'
import { getAccountDetails, updateAccountAvatar, updateOwnAccountDetails, uploadAccountAvatar } from 'actions/user-actions'
import {
  AccountDetails,
  AccountDetailsFetchType,
  CoreAccountDetails,
  CoreFieldKeyApi,
} from 'types/global'
import DateSelectGroup from 'components/generic/DateSelectGroup'
import ScrollToFormError from 'components/generic/ScrollToFormError'
import { EditProfileForm } from 'types/forms/my-account'
import FormInputGroup from 'components/generic/FormInputGroup'
import { makeValidationFunc } from 'forms/validation/validators'
import { useMyDetailsValidationSchema } from 'forms/validation/schemas'
import SexInputGroup from 'components/generic/SexInputGroup'
import { PLACEHOLDERS, useFormLabels } from 'forms/common/translations'
import { FormikNames } from 'types/forms/formik-names'
import AccountSection from './AccountSection'
import LoadingSpinnerWithContainer from 'components/generic/LoadingSpinnerWithContainer'
import moment from 'moment'
import { MIN_AGE_FOR_AVATAR_UPLOAD } from 'constants/profile'
import { BiographicalInformation } from 'types/api/types'
import EditableAvatar from 'components/profile/EditableAvatar'
import { selectAccountAvatarUpdating } from 'redux/selectors'

interface PlayerProfileProps {
  values: EditProfileForm
  editableFields?: CoreFieldKey[]
  fieldsAsLabels?: CoreFieldKey[]
  formikNamesMap?: CoreAccountDetails
}

const CORE_FIELDS_VALUE_NAMES: CoreAccountDetails = {
  nativeGivenName: FormikNames.NATIVE_GIVEN_NAME,
  nativeFamilyName: FormikNames.NATIVE_FAMILY_NAME,
  birthDate: FormikNames.BIRTH_DATE,
  sex: FormikNames.SEX,
  nationalityCode: FormikNames.NATIONALITY_CODE,
  countryOfResidenceCode: FormikNames.COUNTRY_OF_RESIDENCE_CODE,
}

// rendering all core fields - save for countryOfResidenceCode (if editable) - as text for Fast Track launch
const FIELDS_AS_LABELS = Object.values(CoreFieldKeyApi)

const PlayerProfile = ({
  values,
  editableFields = [],
  fieldsAsLabels = [],
  formikNamesMap = CORE_FIELDS_VALUE_NAMES,
}: PlayerProfileProps) => {
  const labels = useFormLabels()
  const {
    nativeGivenName: givenNameValueRef,
    nativeFamilyName: familyNameValueRef,
    birthDate: birthDateValueRef,
    sex: sexValueRef,
    nationalityCode: nationalityCodeValueRef,
    countryOfResidenceCode: residenceCodeValueRef,
  } = formikNamesMap
  const { activeCountriesList, getNationality, getCountryName } = useCountries()
  const residenceOptions = useMemo(() => {
    return activeCountriesList
      .map(country => ({
        value: country['alpha-3'],
        label: country.name || '',
      }))
      .sort((a: Option, b: Option) => a.label.localeCompare(b.label))
  }, [activeCountriesList])
  const nationalityOptions = useMemo(() => {
    return activeCountriesList
      .map(country => ({
        value: country['alpha-3'],
        label: country.nationality || '',
      }))
      .sort((a: Option, b: Option) => a.label.localeCompare(b.label))
  }, [activeCountriesList])

  return (
    <div className={`${styles.contentInner} ${styles.playerProfile}`}>
      <h3>{i18n.t('strMyAccount.playerProfile')}</h3>
      {editableFields.length < Object.keys(CoreFieldsMapping).length && (
        <InfoBox className={styles.infoBox}>
          <p>
            {i18n.t('strMyAccount.infoText')}{' '}
            <a href='/eng/contact' target='_blank' rel='noopener noreferrer'>
              {i18n.t('strMyAccount.contactUs')}
            </a>
          </p>
        </InfoBox>
      )}
      <div className={styles.playerProfileGroups}>
        <FormInputGroup
          label={labels.givenName}
          formikName={givenNameValueRef}
          valueStaticText={values[givenNameValueRef]}
          fieldAsStaticText={fieldsAsLabels.includes(CoreFieldKeyApi.NATIVE_GIVEN_NAME)}
          classWrapper={styles.inputGroup}
        >
          <InputWTN
            name={givenNameValueRef}
            className={styles.commonTextInput}
            disabled={!editableFields.includes(CoreFieldKeyApi.NATIVE_GIVEN_NAME)}
          />
        </FormInputGroup>

        <FormInputGroup
          label={labels.familyName}
          formikName={familyNameValueRef}
          valueStaticText={values[familyNameValueRef]}
          fieldAsStaticText={fieldsAsLabels.includes(CoreFieldKeyApi.NATIVE_FAMILY_NAME)}
          classWrapper={styles.inputGroup}
        >
          <InputWTN
            name={familyNameValueRef}
            className={styles.commonTextInput}
            disabled={!editableFields.includes(CoreFieldKeyApi.NATIVE_FAMILY_NAME)}
          />
        </FormInputGroup>

        <FormInputGroup
          label={labels.dateOfBirth}
          formikName={birthDateValueRef}
          fieldAsStaticText={fieldsAsLabels.includes(CoreFieldKeyApi.BIRTH_DATE)}
          classWrapper={styles.inputGroup}
          valueStaticText={
            isFormattedDateValid(values[birthDateValueRef])
              ? formatUiDate(values[birthDateValueRef])
              : values[birthDateValueRef]
          }
        >
          <DateSelectGroup
            valueKeys={{
              fullDate: birthDateValueRef,
              day: FormikNames.DOB_DAY,
              month: FormikNames.DOB_MONTH,
              year: FormikNames.DOB_YEAR,
            }}
            isDisabled={!editableFields.includes(CoreFieldKeyApi.BIRTH_DATE)}
          />
        </FormInputGroup>

        <FormInputGroup
          label={labels.sex}
          formikName={sexValueRef}
          fieldAsStaticText={fieldsAsLabels.includes(CoreFieldKeyApi.SEX)}
          classWrapper={styles.inputGroup}
          valueStaticText={values[sexValueRef] === SexEnum.M ? labels.male : labels.female}
        >
          <SexInputGroup
            formikName={sexValueRef}
            isDisabled={!editableFields.includes(CoreFieldKeyApi.SEX)}
          />
        </FormInputGroup>

        <FormInputGroup
          label={labels.nationality}
          formikName={nationalityCodeValueRef}
          fieldAsStaticText={fieldsAsLabels.includes(CoreFieldKeyApi.NATIONALITY_CODE)}
          classWrapper={styles.inputGroup}
          valueStaticText={getNationality(values[nationalityCodeValueRef])}
        >
          <SearchFieldWTN
            options={nationalityOptions}
            name={nationalityCodeValueRef}
            className={styles.selectMyDetails}
            placeholderText={PLACEHOLDERS.SEARCH_FIELD.NATIONALITY}
            disabled={!editableFields.includes(CoreFieldKeyApi.NATIONALITY_CODE)}
          />
        </FormInputGroup>

        <FormInputGroup
          label={labels.countryOfResidence}
          formikName={residenceCodeValueRef}
          fieldAsStaticText={!editableFields.includes(CoreFieldKeyApi.COUNTRY_OF_RESIDENCE_CODE)}
          classWrapper={styles.inputGroup}
          valueStaticText={getCountryName(values[residenceCodeValueRef])}
        >
          <SearchFieldWTN
            options={residenceOptions}
            name={residenceCodeValueRef}
            className={styles.selectMyDetails}
            placeholderText={PLACEHOLDERS.SEARCH_FIELD.COUNTRY_OF_RESIDENCE}
            disabled={!editableFields.includes(CoreFieldKeyApi.COUNTRY_OF_RESIDENCE_CODE)}
          />
        </FormInputGroup>
      </div>
    </div>
  )
}

const PlayerInfo = () => {
  const [selectedUnit, setSelectedUnit] = useState<UnitSystem>(UnitSystem.METRIC)
  const optionPressed = (i: number) => {
    setSelectedUnit(HEIGHT_UNIT_OPTIONS[i].key)
  }

  return (
    <div className={styles.contentInner}>
      <h3>{i18n.t('strMyAccount.playerInfo')}</h3>

      <FormInputGroup
        label={i18n.t('strEditProfile.inputHeight')}
        formikName={FormikNames.HEIGHT_IN_METERS}
        classWrapper={styles.inputGroup}
      >
        <div className={styles.unitBtns}>
          {HEIGHT_UNIT_OPTIONS.map(({ name, key }, i) => (
            <BtnToggle isActive={selectedUnit === key} key={key} onClick={() => optionPressed(i)}>
              {name}
            </BtnToggle>
          ))}
        </div>
        <HeightUnitInputs unitSystem={selectedUnit} />
      </FormInputGroup>

      <FormInputGroup
        label={
          <p
            id={FormikNames.PLAYING_HAND}
            data-formik-error={FormikNames.PLAYING_HAND}
            className={styles.formLabel}
          >
            {i18n.t('strEditProfile.inputPreferredHand')}
          </p>
        }
        formikName={FormikNames.PLAYING_HAND}
        classWrapper={styles.inputGroup}
      >
        <div role='group' aria-labelledby={FormikNames.PLAYING_HAND}>
          <RadioFieldWTN
            name={FormikNames.PLAYING_HAND}
            value={PlayingHandEnum.L}
            text={i18n.t('strEditProfile.optsPreferredHandLeft')}
          />
          <RadioFieldWTN
            name={FormikNames.PLAYING_HAND}
            value={PlayingHandEnum.R}
            text={i18n.t('strEditProfile.optsPreferredHandRight')}
          />
        </div>
      </FormInputGroup>

      <FormInputGroup
        label={
          <p
            id={FormikNames.DOUBLE_PLAYING_HAND}
            data-formik-error={FormikNames.DOUBLE_PLAYING_HAND}
            className={styles.formLabel}
          >
            {i18n.t('strEditProfile.inputBackhand')}
          </p>
        }
        formikName={FormikNames.DOUBLE_PLAYING_HAND}
        classWrapper={styles.inputGroup}
      >
        <div role='group' aria-labelledby={FormikNames.DOUBLE_PLAYING_HAND}>
          <RadioFieldWTN
            name={FormikNames.DOUBLE_PLAYING_HAND}
            value={PlayingDoubleHandEnum.NO}
            text={i18n.t('strEditProfile.optsBackhandSingle')}
          />
          <RadioFieldWTN
            name={FormikNames.DOUBLE_PLAYING_HAND}
            value={PlayingDoubleHandEnum.BH}
            text={i18n.t('strEditProfile.optsBackhandTwo')}
          />
        </div>
      </FormInputGroup>

      <FormInputGroup
        label={i18n.t('strEditProfile.inputFavouriteShot')}
        formikName={FormikNames.FAVOURITE_SHOT}
        classWrapper={styles.inputGroup}
      >
        <SelectWTN
          options={FAVOURITE_SHOT_OPTIONS}
          className={styles.selectMyDetails}
          placeholderText={i18n.t('strEditProfile.inputFavouriteShotPlaceholder')}
          name={FormikNames.FAVOURITE_SHOT}
          isSearchable={false}
        />
      </FormInputGroup>
    </div>
  )
}

const MyDetailsWrapper = ({ children }) => {
  return (
    <AccountSection
      title={i18n.t('strMyAccount.myDetails')}
      subtitle={i18n.t('strMyAccount.accountUpToDate')}
      classWrapper={styles.myDetails}
      classBoby={styles.content}
    >
      {children}
    </AccountSection>
  )
}

interface MyDetailsInnerProps {
  accountDetails: AccountDetails
}

const MyDetailsInner = ({ accountDetails }: MyDetailsInnerProps) => {
  const [showSelectAvatarModal, setShowSelectAvatarModal] = useState(false)
  const dispatch = useDispatch()
  const {
    birthDate,
    sex,
    nationalityCode,
    countryOfResidenceCode,
    nativeGivenName,
    nativeFamilyName,
    tennisId,
  } = accountDetails || {}
  const { avatarUrl, doublePlayingHand, favouriteShot, heightInMeters, playingHand } =
    accountDetails?.biographicalInformation || {}
  const editableCoreFields = useMemo(() => {
    // filtering out all core fields save for countryOfResidenceCode (if editable) for Fast Track launch
    return getEditableFields(accountDetails).filter(
      field => field === CoreFieldKeyApi.COUNTRY_OF_RESIDENCE_CODE
    )
  }, [accountDetails])
  const newAccountAvatarLoading = useSelector(selectAccountAvatarUpdating)
  const accountDetailsUpdating = useSelector((state: AppState) => state.user.accountDetailsUpdating)
  const isAvatarUploadDisabled = birthDate
    ? moment().diff(birthDate, 'years') < MIN_AGE_FOR_AVATAR_UPLOAD
    : true
  const myDetailsValidationSchema = useMyDetailsValidationSchema()

  return (
    <>
      <div className={styles.contentInner}>
        <h3>{i18n.t('strMyAccount.avatar')}</h3>

        <div className={styles.avatarSection}>
          <EditableAvatar 
              loadedAvatar={avatarUrl} 
              newAvatarLoading={newAccountAvatarLoading}
              userBirthDate={birthDate}
              appElement='edit-profile'
              onModalUploadFile={(file: File) => dispatch(uploadAccountAvatar(file))}
              onModalUpdateAvatar={(avatarUrl: string) => dispatch(updateAccountAvatar(avatarUrl))}
              currentAvatarUrl={avatarUrl || null}
          />
          <div className={styles.avatarSectionRight}>
            <p className={styles.noMargin}>{i18n.t('strMyAccount.uploadPhotoText')}</p>
            <div className={styles.btnsContainer}>
              <BtnSecondary
                className={styles.editAvatarBtn}
                size='medium'
                onClick={() => setShowSelectAvatarModal(true)}
              >
                {i18n.t('strMyAccount.editAvatar')}
              </BtnSecondary>
              {avatarUrl && (
                <BtnPrimary
                  size='medium'
                  border={false}
                  onClick={() =>
                    dispatch(
                      updateAccountAvatar('')
                    )
                  }
                  className={styles.removeAvatarBtn}
                >
                  {i18n.t('strCommonUI.button.remove')}
                </BtnPrimary>
              )}
            </div>
          </div>
        </div>
        <SelectAvatarModal
          appElement='edit-profile'
          show={showSelectAvatarModal}
          onClose={() => setShowSelectAvatarModal(false)}
          uploadDisabled={isAvatarUploadDisabled}
          onUploadFile={(file: File) => dispatch(uploadAccountAvatar(file))}
          onUpdateAvatar={(avatarUrl: string) => dispatch(updateAccountAvatar(avatarUrl))}
          currentAvatarUrl={avatarUrl || null}
        />
      </div>
      <hr></hr>
      <div className={styles.contentInner}>
        <h3>{i18n.t('strMyAccount.tennisId')}</h3>
        <div className={styles.tennisIdWrapper}>
          <TennisIdIcon /> {tennisId}{' '}
        </div>
      </div>
      <hr></hr>

      <Formik
        enableReinitialize
        initialValues={{
          [FormikNames.NATIVE_GIVEN_NAME]: nativeGivenName || '',
          [FormikNames.NATIVE_FAMILY_NAME]: nativeFamilyName || '',
          [FormikNames.NATIONALITY_CODE]: nationalityCode || '',
          [FormikNames.BIRTH_DATE]: birthDate || '',
          [FormikNames.DOB_DAY]: splitFormattedDate.getDay(birthDate),
          [FormikNames.DOB_MONTH]: splitFormattedDate.getMonth(birthDate),
          [FormikNames.DOB_YEAR]: splitFormattedDate.getYear(birthDate),
          [FormikNames.SEX]: sex || '',
          [FormikNames.COUNTRY_OF_RESIDENCE_CODE]: countryOfResidenceCode || '',
          [FormikNames.FAVOURITE_SHOT]: favouriteShot || '',
          [FormikNames.PLAYING_HAND]: playingHand || PlayingHandEnum.U,
          [FormikNames.DOUBLE_PLAYING_HAND]: doublePlayingHand || PlayingDoubleHandEnum.U,
          ...initializeHeightValues(heightInMeters || 0),
        }}
        validateOnChange={false}
        validateOnBlur={false}
        validate={makeValidationFunc(
          [...editableCoreFields, FormikNames.HEIGHT_IN_METERS],
          myDetailsValidationSchema
        )}
        onSubmit={(values, actions) => {
          const updatedValues: { biographicalInformation: BiographicalInformation } = {
            biographicalInformation: {},
          }

          for (let key in values) {
            //@ts-ignore
            if (editableCoreFields.includes(key)) {
              updatedValues[key] = values[key]
            } else if (EDITABLE_BIOGRAPHICAL_FIELDS.includes(key)) {
              updatedValues.biographicalInformation[key] = values[key]
            }
          }

          /* The following code prevents favouriteShot to be submitted if it is not selected. This code can be reviewed when the favouriteShot enum will have an 'Unknown' option. */
          if (updatedValues.biographicalInformation.favouriteShot === '')
            delete updatedValues.biographicalInformation.favouriteShot

          dispatch(updateOwnAccountDetails(updatedValues))
        }}
      >
        {({ values }) => (
          <Form className={styles.form}>
            <PlayerProfile
              values={values}
              editableFields={editableCoreFields}
              fieldsAsLabels={FIELDS_AS_LABELS}
            />
            <hr></hr>
            <PlayerInfo />
            <hr></hr>
            <BtnPrimary
              size={'medium'}
              type='submit'
              fullWidthAtMobile
              isDisabled={accountDetailsUpdating}
            >
              {i18n.t('strCommonUI.button.updateDetails')}
            </BtnPrimary>
            <ScrollToFormError />
          </Form>
        )}
      </Formik>
    </>
  )
}

const MyDetails = () => {
  const dispatch = useDispatch()
  const { accountDetails, accountDetailsError, accountDetailsLoading, tennisId } = useSelector(
    (state: AppState) => state.user
  )

  useEffect(() => {
    if (tennisId) {
      dispatch(getAccountDetails(AccountDetailsFetchType.COMPLETE))
    }
  }, [dispatch, tennisId])

  if (accountDetailsError)
    return (
      <MyDetailsWrapper>
        <InfoBox textOnly iconColor='red' boxColor='red'>
          {i18n.t('strMyAccount.infoError')}
        </InfoBox>
      </MyDetailsWrapper>
    )

  if (!accountDetails || accountDetailsLoading)
    return (
      <MyDetailsWrapper>
        <LoadingSpinnerWithContainer />
      </MyDetailsWrapper>
    )

  return (
    <MyDetailsWrapper>
      <MyDetailsInner accountDetails={accountDetails as AccountDetails} />
    </MyDetailsWrapper>
  )
}

export default MyDetails
